'use client';
import { analiticaStatusPageEvents } from '@payments-checkout/shared/plugins/analitica/statusPage.events';
import {
  Alert,
  Button,
  ProgressIndicator,
  Skeleton,
  Text,
} from '@stone-payments/jade';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useExpirationProgress } from '../../hooks/useExpirationProgress';
import { CopyButton } from '../copyButton';
import { isExpired } from '../trackingStatus/expirationHelper';
import { ExpiredBoletoIcon } from './expiredBoletoIcon';

interface BoletoTrackingStatusProps {
  barcodeImg?: string;
  barcodeText?: string;
  expirationDate?: string;
  documentUrl?: string;
}

const BARCODE_IMG_SIZE_CLASSNAME =
  'w-[265px] h-[34px] md:w-[329px] md:h-[50px]';

export function BoletoTrackingStatus({
  barcodeImg,
  barcodeText,
  expirationDate,
  documentUrl,
}: BoletoTrackingStatusProps = {}) {
  const { formatMessage } = useIntl();
  const { progressValue, timeRemaining } =
    useExpirationProgress(expirationDate);
  const expired = isExpired(expirationDate);
  const [isBarcodeLoaded, setIsBarcodeLoaded] = useState(false);

  const handleDownloadBoleto = () => {
    window.open(documentUrl, '_blank');
    analiticaStatusPageEvents.events.statusPage.downloadBoletoClicked();
  };

  return (
    <div className="w-full space-y-jade-300">
      <div className="text-center px-jade-200 py-jade-300">
        <div className="text-center mb-jade-150">
          <Text
            color="current-color"
            variant="heading-medium"
            className="text-center"
          >
            {expired
              ? formatMessage({ id: 'boleto.heading.expired' })
              : formatMessage({ id: 'boleto.heading' })}
          </Text>
        </div>

        <Text variant="text-large" color="medium" className="text-center">
          {expired
            ? formatMessage({ id: 'boleto.instruction.expired' })
            : formatMessage({ id: 'boleto.instruction' })}
        </Text>
        {expired ? (
          <div className="mb-jade-400 mt-jade-400 flex flex-col items-center gap-jade-50">
            <div className="mb-jade-300">
              <ExpiredBoletoIcon />
            </div>
          </div>
        ) : (
          <>
            {barcodeImg && (
              <div className="mb-jade-400 mt-jade-400 flex flex-col items-center gap-jade-50 bg-jade-background-surface p-jade-200 border-jade-xsmall border-jade-border-low border-solid rounded-jade-small">
                {!isBarcodeLoaded && (
                  <Skeleton
                    data-testid="boleto-barcode-skeleton"
                    className={BARCODE_IMG_SIZE_CLASSNAME}
                  />
                )}
                <img
                  src={barcodeImg}
                  alt="Boleto barcode"
                  onLoad={() => setIsBarcodeLoaded(true)}
                  onError={() => setIsBarcodeLoaded(true)}
                  className={`${BARCODE_IMG_SIZE_CLASSNAME} md:mb-jade-100 ${
                    isBarcodeLoaded ? '' : 'hidden'
                  }`}
                />
                {barcodeText && (
                  <Text
                    color="high"
                    variant="text-small"
                    weight="semibold"
                    className="text-center break-all"
                  >
                    {barcodeText}
                  </Text>
                )}
              </div>
            )}

            <div className="flex flex-col items-center gap-jade-300 mt-jade-150">
              <div className="flex flex-col gap-jade-100 md:flex-row md:gap-jade-300 w-full">
                <div className="flex-1">
                  <CopyButton
                    textToCopy={barcodeText || ''}
                    onCopy={() =>
                      analiticaStatusPageEvents.events.statusPage.copyBoletoCodeClicked()
                    }
                  >
                    {formatMessage({ id: 'statusPage.copyButton' })}
                  </CopyButton>
                </div>
                <div className="flex-1">
                  <Button
                    icon="barcode"
                    size="medium"
                    variant="primary-solid"
                    className="w-full"
                    onClick={handleDownloadBoleto}
                  >
                    {formatMessage({ id: 'boleto.download' })}
                  </Button>
                </div>
              </div>
              <ProgressIndicator
                value={progressValue}
                hideValue
                hideLabel
                size="large"
                variant="bar"
                supportText={`${formatMessage({
                  id: 'boleto.waitingPayment',
                })} ${timeRemaining}`}
                label={`${formatMessage({
                  id: 'boleto.waitingPayment',
                })} ${timeRemaining}`}
              />
            </div>
          </>
        )}

        <div className="flex flex-col items-center gap-jade-300 mt-jade-300">
          <Alert
            variant="info"
            showIcon
            description={formatMessage({ id: 'boleto.info' })}
          />
        </div>
      </div>
    </div>
  );
}
